import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { GridApi } from 'ag-grid-community';
import type { UrlInventoryRow } from '../api/types';
import {
  hasRecentStatusChange,
  urlInventoryColumnConfig,
} from '../config/urlInventoryColumnConfig';
import { INDEXER_AG_GRID_HEIGHT, IndexerAgGrid } from './IndexerAgGrid';
import { IndexerIcon } from './IndexerIcon';
import { UrlActivityModal } from './UrlActivityModal';

export function UrlInventoryTable({
  projectId,
  items,
  selected,
  onSelectedChange,
}: {
  projectId: string;
  items: UrlInventoryRow[];
  selected: Set<string>;
  onSelectedChange: (next: Set<string>) => void;
}) {
  const navigate = useNavigate();
  const apiRef = useRef<GridApi<UrlInventoryRow> | null>(null);
  const syncingSelection = useRef(false);
  const [detailUrl, setDetailUrl] = useState<string | null>(null);
  const detailRow = useMemo(
    () => items.find((row) => row.url === detailUrl) ?? null,
    [items, detailUrl],
  );

  const columns = useMemo(
    () =>
      urlInventoryColumnConfig({
        navigate,
        onDetail: (url) => setDetailUrl(url),
      }),
    [navigate],
  );

  const applySelectionToGrid = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    syncingSelection.current = true;
    api.forEachNode((node) => {
      const url = node.data?.url;
      if (!url) return;
      node.setSelected(selected.has(url), false);
    });
    syncingSelection.current = false;
  }, [selected]);

  useEffect(() => {
    applySelectionToGrid();
  }, [applySelectionToGrid, items]);

  const otherOptions = useMemo(
    () => ({
      getRowId: (params: { data: UrlInventoryRow }) => params.data.url,
      rowHeight: 56,
      rowSelection: {
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: true,
        enableClickSelection: false,
      },
      animateRows: false,
      getRowClass: (params: { data?: UrlInventoryRow }) => {
        if (!params.data) return undefined;
        const classes: string[] = [];
        switch (params.data.indexBucket) {
          case 'indexed':
            classes.push('indexer-url-bucket-indexed');
            break;
          case 'excluded':
            classes.push('indexer-url-bucket-excluded');
            break;
          case 'error':
            classes.push('indexer-url-bucket-error');
            break;
          default:
            classes.push('indexer-url-bucket-unknown');
        }
        if (hasRecentStatusChange(params.data)) {
          classes.push('indexer-url-changed');
        }
        return classes.join(' ');
      },
      onGridReady: (event: { api: GridApi<UrlInventoryRow> }) => {
        apiRef.current = event.api;
        applySelectionToGrid();
      },
      onFirstDataRendered: () => {
        applySelectionToGrid();
      },
      onSelectionChanged: () => {
        if (syncingSelection.current) return;
        const api = apiRef.current;
        if (!api) return;
        const urls = api.getSelectedRows().map((row) => row.url);
        onSelectedChange(new Set(urls));
      },
      onCellClicked: (event: {
        colDef?: { field?: string };
        data?: UrlInventoryRow;
      }) => {
        if (event.colDef?.field !== 'url' || !event.data?.url) return;
        setDetailUrl(event.data.url);
      },
    }),
    [applySelectionToGrid, onSelectedChange],
  );

  if (items.length === 0) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center gap-2 text-center text-secondary py-5">
        <IndexerIcon name="link-45deg" />
        <p className="mb-0">آدرسی با این فیلتر پیدا نشد.</p>
      </div>
    );
  }

  return (
    <>
      <IndexerAgGrid
        tableName="indexer_url_inventory"
        height={INDEXER_AG_GRID_HEIGHT}
        columns={columns}
        data={items}
        otherOptions={otherOptions}
      />

      {detailUrl && (
        <UrlActivityModal
          projectId={projectId}
          url={detailUrl}
          show
          inspectCount={detailRow?.inspectCount ?? 0}
          onHide={() => setDetailUrl(null)}
        />
      )}
    </>
  );
}
